import { AlertTriangle } from 'lucide-react';
import { colors } from '../styles/colors';
import ActionButton from './ActionButton';
import ModalPortal from './ModalPortal';

/**
 * ConfirmationModal - Modale de confirmation réutilisable
 *
 * @param {Object} props
 * @param {boolean} props.isOpen - État d'ouverture de la modale
 * @param {function} props.onClose - Callback de fermeture (annulation)
 * @param {function} props.onConfirm - Callback de confirmation
 * @param {string} props.title - Titre de la modale
 * @param {string} props.message - Message de confirmation
 * @param {string} [props.confirmText='Confirmer'] - Texte du bouton de confirmation
 * @param {string} [props.cancelText='Annuler'] - Texte du bouton d'annulation
 * @param {string} [props.variant='primary'] - Variante du bouton de confirmation (primary, warning, danger)
 * @param {boolean} [props.loading=false] - État de chargement
 */
function ConfirmationModal({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  confirmText = 'Confirmer',
  cancelText = 'Annuler',
  variant = 'primary',
  loading = false,
  invertButtons = false
}) {
  if (!isOpen) return null;

  return (
    <ModalPortal>
      <div
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1100,
          fontFamily: '"Noto Sans", sans-serif'
        }}
        onClick={onClose}
      >
        <div
          style={{
            backgroundColor: colors.text.white,
            borderRadius: '1rem',
            width: '90%',
            maxWidth: '450px',
            boxShadow: '0 10px 40px rgba(0, 0, 0, 0.2)',
            overflow: 'hidden'
          }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div
            style={{
              backgroundColor: variant === 'warning' ? colors.primary.gold : colors.primary.teal,
              color: colors.text.white,
              padding: '1rem 1.25rem',
              display: 'flex',
              alignItems: 'center',
              gap: '0.75rem'
            }}
          >
            <AlertTriangle size={24} />
            <h3
              style={{
                margin: 0,
                fontSize: '1.125rem',
                fontWeight: '700',
                fontFamily: '"Century Gothic Bold", "Noto Sans", sans-serif'
              }}
            >
              {title}
            </h3>
          </div>

          {/* Contenu */}
          <div style={{ padding: '1.5rem' }}>
            <p
              style={{
                margin: '0 0 1.5rem 0',
                fontSize: '0.9375rem',
                color: colors.primary.gray,
                lineHeight: '1.5'
              }}
            >
              {message}
            </p>

            {/* Boutons d'action */}
            <div
              style={{
                display: 'flex',
                gap: '0.75rem',
                justifyContent: 'flex-end'
              }}
            >
              {cancelText !== null && (
                <ActionButton
                  text={cancelText}
                  onClick={onClose}
                  variant={invertButtons ? variant : 'secondary'}
                  disabled={loading}
                />
              )}
              <ActionButton
                text={confirmText}
                onClick={onConfirm}
                variant={invertButtons ? 'secondary' : variant}
                disabled={loading}
              />
            </div>
          </div>
        </div>
      </div>
    </ModalPortal>
  );
}

export default ConfirmationModal;
